import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import "react-datepicker/dist/react-datepicker.css";
import Login from "./components/Auth/Login";
import Chat from "./components/Communication/Chat/Chat.jsx";
import Channels from "./components/Communication/Channels/Channels.jsx";
import Dashboard from "./components/Dashboard/Dashboard";
import Layout from "./components/Dashboard/shared/layout";
import Logo from "./assets/logoTrans.png";
import VerifyEmail from "./components/Auth/VerifyEmail";
import TasksHome from "./screens/TasksHome.jsx";
import Kanban from "./screens/Kanban.jsx";
import ResetPassword from "./components/Auth/ResetPassword";
import VideoCall from "./components/Communication/Meetings/VideoCall/MeetingHome.jsx";
import UserProfile from "./components/Auth/Users/UserProfile";
import Calendar from "./components/Calendar/New Calendar/CalendarView.jsx";
import ProjectsDashboard from "./components/Projects/ProjectsDashboard.jsx";
import ProjectDetails from "./components/Projects/ProjectDetails.jsx";
import AddProject from "./components/Projects/AddProject.jsx";
import InvitationPage from "./components/Projects/InvitationPage";
import EditProject from "./components/Projects/EditProject";
import Help from "./components/Dashboard/Help";
import { auth } from "./components/Auth/firebase";
import { ProjectsProvider } from "./context/ProjectsContext.js";
import { AuthProvider } from "./context/AuthContext";
import FetchData from "./utils/allData.jsx";
import CollabAI from "./components/CollabAI/Chat.jsx";
import "./App.css";

function LoadingScreen() {
  return (
    <div className="flex h-screen items-center justify-center">
      <img
        src={Logo}
        alt="Logo"
        className="w-[350px] animate-pulse opacity-0 transition-opacity duration-500 ease-in-out"
      />
    </div>
  );
}

function MobileMessage() {
  return (
    <div className="flex h-screen flex-col items-center justify-center text-center p-4">
      <img src={Logo} alt="Your Logo" className="w-[250px] mb-4" />
      <h1 className="text-2xl font-semibold">Use Desktop Version</h1>
      <p className="text-lg">
        Our web app is designed for desktop use. Please access it from a desktop
        browser.
      </p>
      <p className="text-lg mt-2">Our Android and iOS apps are coming soon!</p>
    </div>
  );
}

function NoInternetMessage() {
  return (
    <div className="flex h-screen flex-col items-center justify-center text-center p-4">
      <h1 className="text-2xl font-semibold">No Internet Connection</h1>
      <p className="text-lg">
        It looks like you are offline. Please check your internet connection.
      </p>
    </div>
  );
}

// Function to check internet connection status
async function checkInternetConnection() {
  // Check if the hostname is localhost
  if (
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
  ) {
    return true; // Assume internet connection is available on localhost
  }

  try {
    const response = await fetch("https://collabpro-v1.vercel.app", {
      // Use the appropriate method and timeout value
      method: "HEAD",
      mode: "no-cors",
      cache: "no-store",
      headers: {
        "Content-Type": "text/plain",
      },
    });
    return response.ok;
  } catch (error) {
    return false;
  }
}

function App() {
  const [user, setUser] = useState(null);
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isOnline, setIsOnline] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUser(user);
        setUserName(user.displayName);
      } else {
        setUser(null);
        setUserName("");
      }
      setLoading(false);
    });

    // Detect if the user is on a mobile device with screen width up to 600px (approximately 7")
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 600);
    };

    // Check initial mobile state
    checkIfMobile();
    // Add event listener to detect window resize
    window.addEventListener("resize", checkIfMobile);

    // Function to update online status
    const updateOnlineStatus = async () => {
      const isOnline = await checkInternetConnection();
      setIsOnline(isOnline);
      console.log("Online status changed:", isOnline);
    };

    // Call the function to check initial online status
    updateOnlineStatus();

    // Add event listeners to detect online and offline status
    window.addEventListener("online", updateOnlineStatus);
    window.addEventListener("offline", updateOnlineStatus);

    // Periodically check internet connection every 5 minutes
    const intervalId = setInterval(updateOnlineStatus, 5 * 60 * 1000); // 5 minutes

    // Cleanup function
    return () => {
      clearInterval(intervalId);
      unsubscribe();
      window.removeEventListener("resize", checkIfMobile);
      window.removeEventListener("online", updateOnlineStatus);
      window.removeEventListener("offline", updateOnlineStatus);
    };
  }, []);

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isOnline) {
    return <NoInternetMessage />;
  }

  if (isMobile) {
    return <MobileMessage />;
  }

  return (
    <div className="App">
      <AuthProvider>
        <ProjectsProvider>
          <Router>
            <Routes>
              <Route
                path="/login"
                element={user ? <Navigate to="/" /> : <Login />}
              />
              <Route
                path="/"
                element={
                  user && user.emailVerified ? (
                    <Layout />
                  ) : user ? (
                    <Navigate to="/verify-email" />
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="/dms" element={<Chat />} />
                <Route path="/communication/dms" element={<Chat />} />
                <Route path="/communication/channels" element={<Channels />} />
                <Route path="/communication/meetings" element={<VideoCall />} />
                <Route path="/tasks/*" element={<TasksHome />} />
                <Route path="/tasks/board/:boardId" element={<Kanban />} />
                <Route path="/profile" element={<UserProfile />} />
                <Route path="/calendar" element={<Calendar />} />
                <Route path="/projects" element={<ProjectsDashboard />} />
                <Route path="/export" element={<FetchData />} />
                <Route path="/collabAI" element={<CollabAI />} />
                <Route
                  path="/projects/:projectId"
                  element={<ProjectDetails />}
                />
                <Route
                  path="/projects/:projectId/boards/:boardId"
                  element={<Kanban />}
                />
                <Route path="/projects/add" element={<AddProject />} />
                <Route
                  path="projects/edit-project/:id"
                  element={<EditProject />}
                />
                <Route path="/help" element={<Help />} />
              </Route>
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/reset-password" element={<ResetPassword />} />
            </Routes>
          </Router>
        </ProjectsProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
